import { useState, useRef, useEffect } from 'react';

const phases = [
  {
    id: 'understand',
    number: '01',
    title: 'Understand',
    tagline: 'Country and cultural context',
    icon: 'ri-eye-line',
    color: 'bg-[#E48523]',
    lessons: [
      { num: 1, title: 'Introduction to Indigenous land and life connections' },
      { num: 2, title: 'The significance of Country in Indigenous culture' },
      { num: 3, title: 'Understanding both the Physical and Spiritual connections to Country for Indigenous peoples' },
      { num: 4, title: 'Introduction to Welcome and Acknowledgement of Country' },
      { num: 5, title: 'Understanding the Better Placed Framework' },
      { num: 6, title: 'Understanding the role of Elders in Indigenous design' },
      { num: 7, title: 'Cultural safety and Indigenous intellectual property' },
    ],
  },
  {
    id: 'engage',
    number: '02',
    title: 'Engage',
    tagline: 'Relationships and community involvement',
    icon: 'ri-shake-hands-line',
    color: 'bg-[#BA3D03]',
    lessons: [
      { num: 8, title: 'Building relationships with Indigenous communities' },
      { num: 9, title: 'Determining community involvement levels' },
    ],
  },
  {
    id: 'integrate',
    number: '03',
    title: 'Integrate',
    tagline: 'Country centred approaches in projects',
    icon: 'ri-git-merge-line',
    color: 'bg-[#3D1203]',
    lessons: [
      { num: 10, title: 'Adopting a Country centred approach' },
      { num: 11, title: 'Practices for enabling a new approach' },
      { num: 12, title: 'Implementing the framework through action' },
      { num: 13, title: 'Design considerations and case studies' },
    ],
  },
  {
    id: 'sustain',
    number: '04',
    title: 'Sustain',
    tagline: 'Outcomes and continuous improvement',
    icon: 'ri-seedling-line',
    color: 'bg-[#E48523]',
    lessons: [
      { num: 14, title: 'Outcomes for Country and community' },
      { num: 15, title: 'Key outcomes of the framework' },
    ],
  },
];

function AnimatedPhaseCard({
  phase,
  index,
  isOpen,
  onToggle,
}: {
  phase: typeof phases[number];
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [bodyHeight, setBodyHeight] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      setBodyHeight(bodyRef.current.scrollHeight);
    }
  }, [isOpen]);

  return (
    <div
      ref={ref}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.55s ease ${index * 0.1}s, transform 0.55s ease ${index * 0.1}s`,
      }}
      className={`rounded-xl border backdrop-blur-md transition-all duration-300 ${
        isOpen
          ? 'border-[#E48523]/60 shadow-lg shadow-black/30 bg-white/12'
          : 'border-white/15 bg-white/8 hover:bg-white/12 hover:border-white/25'
      }`}
    >
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-5 px-7 py-5 text-left cursor-pointer group"
      >
        <div className={`w-10 h-10 ${phase.color} rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
          <i className={`${phase.icon} text-lg text-white`}></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-bold text-[#F5C97A] uppercase tracking-wide">Phase {phase.number}</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <span className="text-lg font-bold text-white">{phase.title}</span>
            <span className="hidden sm:block text-white/30">·</span>
            <span className="text-sm text-white/60">{phase.tagline}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs font-semibold text-[#F5C97A] bg-[#E48523]/20 border border-[#E48523]/30 px-2.5 py-1 rounded-full whitespace-nowrap">
            {phase.lessons.length} lessons
          </span>
          <i className={`ri-arrow-down-s-line text-xl text-white/50 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}></i>
        </div>
      </button>

      {/* Accordion Body — smooth height animation */}
      <div
        style={{
          height: isOpen ? bodyHeight : 0,
          overflow: 'hidden',
          transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div ref={bodyRef} className="px-7 pb-6">
          <div className="border-t border-white/15 pt-5 space-y-2">
            {phase.lessons.map((lesson) => (
              <div
                key={lesson.num}
                className="flex items-start gap-4 py-3 px-4 rounded-lg hover:bg-white/10 transition-colors"
              >
                <span className="w-7 h-7 flex items-center justify-center rounded-full bg-[#E48523]/30 text-[#F5C97A] text-xs font-bold flex-shrink-0 mt-0.5">
                  {lesson.num}
                </span>
                <span className="text-white/80 text-sm leading-relaxed">{lesson.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ModuleBreakdown() {
  const [openPhase, setOpenPhase] = useState<string>('understand');

  const headerRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setHeaderVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeaderVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="module-breakdown"
      className="py-20 sm:py-28 relative overflow-hidden"
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/a9e3451ca2869e4e7fbef042186f0654.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'scroll',
      }}
    >
      {/* Layered overlay for readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#3D1203]/80 via-[#1a0800]/75 to-[#3D1203]/85 pointer-events-none" />

      {/* Dot-pattern texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, rgba(245,201,122,0.18) 1px, transparent 1px)`,
          backgroundSize: '22px 22px',
          maskImage: 'radial-gradient(ellipse 100% 100% at 50% 50%, black 40%, transparent 100%)',
          WebkitMaskImage: 'radial-gradient(ellipse 100% 100% at 50% 50%, black 40%, transparent 100%)',
        }}
      />

      <div className="absolute inset-0 bg-[#E48523]/10 pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-10 sm:mb-14"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(24px)',
            transition: 'opacity 0.6s ease, transform 0.6s ease',
          }}
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#E48523]/20 text-[#F5C97A] border border-[#E48523]/30 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm">
            Course Lessons Overview
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5">
            Course Lessons Overview
          </h2>
          <p className="text-lg text-white/70 max-w-2xl mx-auto">
            15 lessons designed for NSW built environment teams, aligned with Better Placed and Connecting with Country.
          </p>
        </div>

        {/* Accordion */}
        <div className="space-y-3">
          {phases.map((phase, index) => (
            <AnimatedPhaseCard
              key={phase.id}
              phase={phase}
              index={index}
              isOpen={openPhase === phase.id}
              onToggle={() => setOpenPhase(openPhase === phase.id ? '' : phase.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
